/**
 * Transforms Excel row data to RoundTable model format
 */

export interface RoundTableCreateData {
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

/**
 * Extracts RT number from subdomain (substring before first dash)
 * @param subdomain - The subdomain string like "123-berlin"
 * @returns The extracted RT number or null if invalid
 */
export const extractRTNumberFromSubdomain = (subdomain: string | undefined | null): number | null => {
    if (!subdomain || typeof subdomain !== 'string') {
        return null;
    }

    const dashIndex = subdomain.indexOf('-');
    if (dashIndex > 0) {
        const numberPart = subdomain.substring(0, dashIndex).trim();
        const parsed = parseInt(numberPart, 10);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

/**
 * Transforms an Excel row to RoundTable create data
 * @param excelRow - The Excel row data with column names as keys
 * @returns RoundTable create data object
 * @throws Error if RT number is missing or invalid
 */
export const transformExcelRowToRoundTable = (excelRow: Record<string, any>): RoundTableCreateData => {
    // Extract RT number from subdomain
    const subdomain = excelRow['[Club] Subdomain'];
    const rtNumber = extractRTNumberFromSubdomain(subdomain);

    if (!rtNumber) {
        throw new Error(`RT-Nummer fehlt oder ungültig für Club "${excelRow['[Club] Name']}" (Subdomain: "${subdomain}")`);
    }

    return {
        name: excelRow['[Club] Name'] || '',
        number: rtNumber,
        iban: excelRow['[Bank Account] IBAN'] || '',
        bic: excelRow['[Bank Account] BIC'] || '',
        kontoInhaber: excelRow['[Bank Account] Owner'] || '',
        bankName: excelRow['[FinOrg] Name'] || '',
        billingName: excelRow['[Treasurer] Name'] || '',
        billingEmail: excelRow['[Treasurer] Email'] || '',
        billingPlz: excelRow['[Treasurer] Postal Code'] || '',
        billingCity: excelRow['[Treasurer] City'] || '',
        billingStreet: excelRow['[Treasurer] Street1'] || '',
        billingStreetNumber: excelRow['[Treasurer] Street2'] || '',
    };
};
